import { useCallback } from 'react';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { ModuleConfigurationAdmin } from '@/types';

interface DeleteModuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  module: ModuleConfigurationAdmin | null;
  onConfirm: (moduleKey: string) => void;
  isDeleting?: boolean | undefined;
}

export function DeleteModuleDialog({
  open,
  onOpenChange,
  module,
  onConfirm,
  isDeleting,
}: DeleteModuleDialogProps) {
  const handleConfirm = useCallback(() => {
    if (!module) return;
    onConfirm(module.moduleKey);
  }, [module, onConfirm]);

  if (!module) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Delete Module Configuration
          </DialogTitle>
          <DialogDescription>
            Permanently remove this module from the system.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-3 border rounded-lg bg-muted/30 mb-4">
            <p className="font-medium">{module.displayName}</p>
            <p className="text-sm text-muted-foreground font-mono">{module.moduleKey}</p>
          </div>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium">This action cannot be undone.</p>
              <ul className="mt-2 list-disc list-inside text-sm space-y-1">
                <li>The module will no longer appear in the migration wizard</li>
                <li>Existing migration history for this module is preserved</li>
                <li>Cannot delete while a migration using this module is in progress</li>
              </ul>
            </AlertDescription>
          </Alert>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={isDeleting}>
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete Module
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
